import React, { useEffect, useState, useRef } from "react"
import { connect } from "react-redux"
import { v4 as uuidv4 } from "uuid"
import dayjs from "dayjs"
import {
    Alert, Button, Card, Col, Container, Form, FormGroup,
    Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row,
} from "reactstrap"
import ReactApexChart from "react-apexcharts"
import { AreaChart, Area as RechartsArea, XAxis, YAxis, ReferenceArea, Legend, ResponsiveContainer } from "recharts"

import Area from "./area"
import PlantSelect from "./PlantSelect"

import { getAreas, updateArea, addArea, deleteArea, fetchCustomPlants, saveCustomPlant, updateCustomPlant, searchGuild } from "../../store/goals/actions"
import { getProfile } from "../../store/auth/profile/actions"
import { getPlantData, getClimateData, updateDesign, getDesigns } from "../../store/design/actions"
import { addNewEvent, deleteEvent } from "../../store/calendar/actions"
import { createJournalEntry, deleteJournalEntry, getJournalTimeline, updateJournalEntry } from "../../store/journal/actions"

const Spline = require("cubic-spline")

const PlantsAreas = (props) => {
    const {
        userareas, profile, plantData, plantMap, customplants, designs, climateData, guildResults,
        timelineNotes,
        onGetAreas, onGetProfile, onGetPlantData, onGetClimateData, onLoadDesigns, onGetCustomPlants,
        onUpdateArea, onAddArea, onDeleteArea, onAddNewEvent, onDeleteEvent, onSaveDesign,
        onUpdateCustomPlant, onSaveCustomPlant, onSearchGuild,
        onCreateJournalEntry, onDeleteJournalEntry, onGetJournalTimeline, onUpdateJournalEntry,
    } = props

    const username = JSON.parse(localStorage.getItem("user") || "{}").user || ""

    // ── state ─────────────────────────────────────────────────────────────────
    const [currYear, setCurrYear]           = useState(new Date().getFullYear())
    const [showCompleted, setShowCompleted] = useState(false)
    const [unitmeasurement, setUnitMeasurement] = useState("imperial")
    const [plantProps, setPlantProps]       = useState({})
    const [plantOptions, setPlantOptions]   = useState([])

    // area management
    const [addAreaModal, setAddAreaModal]       = useState(false)
    const [name, setName]                       = useState("")
    const [space, setTotalSpace]                = useState("")
    const [copyFromPrevious, setCopyFromPrevious] = useState(false)
    const [areaExpand, setAreaExpand]           = useState(Array(20).fill(false))

    // add-plant modal (opened by Area child)
    const [addPlantModal, setAddPlantModal]             = useState(false)
    const [currPlant, setCurrPlant]                     = useState({})
    const [currArea, setCurrArea]                       = useState({ plants: [] })
    const [variety, setVariety]                         = useState("")
    const [numPlants, setNumPlants]                     = useState(1)
    const [plantStatus, setPlantStatus]                 = useState("")
    const [seasonRotation, setSeasonRotation]           = useState("")
    const [plantSource, setPlantSource]                 = useState("")
    const [orderDate, setOrderDate]                     = useState("")
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("")
    const [lastDatePossible, setLastDatePossible]       = useState(false)
    const [succession, setSuccession]                   = useState(false)
    const [successionWeeks, setSuccessionWeeks]         = useState("2")   // "2" | "3" | "4" | "consecutive"
    const [errors, setErrors]                           = useState({})
    const calcMetaRef = useRef(null)   // first-planting dates + window end, captured by calcPlantingCalendar

    // transfer modal
    const [transferModal, setTransferModal]     = useState(false)
    const [transferRows, setTransferRows]       = useState([{ areaId: "", numPlants: "" }])
    const [transferError, setTransferError]     = useState("")
    const [remainingPlants, setRemainingPlants] = useState(0)

    // note / harvest form (passed to Area → Plant)
    const [noteDate, setNoteDate]         = useState("")
    const [notes, setNotes]               = useState("")
    const [noteTags, setNoteTags]         = useState("")
    const [harvestDate, setHarvestDate]   = useState("")
    const [harvestAmount, setHarvestAmount] = useState("")
    const [harvestUnit, setHarvestUnit]   = useState("")

    // planting calendar
    const [climateAverages, setClimateAverages]             = useState([])
    const [plantingCalendarModal, setPlantingCalendarModal] = useState(false)
    const [plantingDates, setPlantingDates]                 = useState(false)
    const [plantingMonths, setPlantingMonths]               = useState([])
    const [annotations, setAnnotations]                     = useState({})

    // recharts planting-window calendar (matches the Edit Card chart)
    const [rechartsClimate, setRechartsClimate]             = useState([])
    const [minCold, setMinCold]                             = useState(null)
    const [maxHot, setMaxHot]                               = useState(null)
    const [windowDates, setWindowDates]                     = useState({ seed: null, harvest: null })

    // "doesn't fit this year" → confirm rolling the plant into next year's area
    const [rollModalOpen, setRollModalOpen]                 = useState(false)
    const [pendingRollPlant, setPendingRollPlant]           = useState(null)
    const [rollTargetYear, setRollTargetYear]               = useState(null)
    const [rollInfo, setRollInfo]                           = useState(null)

    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"]
    const labels = {
        metric:   { weight:"kg",  temperature:"°C", space:"sq. m",  distance:"meters" },
        imperial: { weight:"oz",  temperature:"°F", space:"sq. ft", distance:"inches" },
    }

    const [areas, setAreas] = useState([])

    useEffect(() => {
        const toNum = v => v == null ? 0 : typeof v === "number" ? v : (v?.N != null ? parseFloat(v.N) : parseFloat(v) || 0)
        setAreas(userareas.map(area => ({
            ...area,
            spaceUsed: (area.plants || [])
                .filter(p => p.status !== "Complete" && p.status !== "Failed")
                .reduce((sum, p) => sum + toNum(p.spaceUsed), 0),
        })))
    }, [userareas])

    useEffect(() => {
        if (climateData && Object.keys(climateData).length > 0) {
            setClimateAverages([
                { name: "Hot Averages", data: months.map(m => parseFloat(climateData[m + "Max"])) },
                { name: "Cold Averages", data: months.map(m => parseFloat(climateData[m + "Min"])) },
            ])
        }
    }, [climateData])

    var areaIndices = {}

    const plantingCalendarOptions = {
        chart: { type:"area", stacked:false, height:350, zoom:{ enabled:false } },
        dataLabels: { enabled:false },
        yaxis: { min:0, max:120 },
        xaxis: { type:"datetime", categories: ["2024-01-01","2024-02-01","2024-03-01","2024-04-01","2024-05-01","2024-06-01","2024-07-01","2024-08-01","2024-09-01","2024-10-01","2024-11-01"] },
        tooltip: { x: { format:"dd/MM/yy" } },
        fill: { type:"solid", opacity:0.75 },
    }

    // ── mount ─────────────────────────────────────────────────────────────────
    useEffect(() => {
        onGetProfile(username)
        onGetPlantData()
        onGetAreas()
        onLoadDesigns()
        onGetCustomPlants()
        onGetJournalTimeline({ username })
        setCurrYear(new Date().getFullYear())
    }, [])

    useEffect(() => {
        if (profile?.unitmeasurement?.length > 0) setUnitMeasurement(profile.unitmeasurement)
        if (profile?.address) {
            onGetClimateData(profile.address.zipcode)
            setPlantProps({
                daysToTransplant:"Days For Seedling", numPlants:"Number Of Plants", daysToHarvest:"Days To Harvest",
                germinationTime:"Germination Time",
                minimumTemp:`Minimum Temperature (${labels[unitmeasurement].temperature})`,
                maximumTemp:`Maximum Temperature (${labels[unitmeasurement].temperature})`,
                yield_per_plant_ounces:`Yield Per Plant (${labels[unitmeasurement].weight})`,
                plant_family:"Plant Family", season:"Growing Cycle", size:"Size",
                foodForestLayer:"Food Forest Layer", guildFunction:"Guild Function", climate:"Climate",
                USDAMax:"USDA Max Zone", USDAMin:"USDA Min Zone",
                spacing_between_plants:`Spacing between Plants (${labels[unitmeasurement].distance})`,
            })
        }
    }, [profile])

    useEffect(() => {
        const confirmed = plantData.map(x => ({ value: x.name, text: x.name }))
        const custom    = customplants.map(x => ({ value: x.species, text: `${x.species} (custom)` }))
        setPlantOptions([...confirmed, ...custom])
    }, [plantData, customplants])

    useEffect(() => {
        const total = transferRows.reduce((sum, r) => sum + Number(r.numPlants || 0), 0)
        setRemainingPlants((currPlant.numPlants || 0) - total)
    }, [transferRows, currPlant.numPlants])

    // ── helpers ───────────────────────────────────────────────────────────────
    function findPlantIndices(areas, plantId) {
        for (let i = 0; i < areas.length; i++) {
            const j = areas[i].plants.findIndex(p => p.id === plantId)
            if (j !== -1) return { areaIndex: i, plantIndex: j }
        }
        return { areaIndex: -1, plantIndex: -1 }
    }

    function createNewEvent(plant, title, category, startDate) {
        const ev = { id: uuidv4(), username, title, description: "", plant: plant.plant, start: startDate, category }
        if (plant.events) plant.events.push(ev.id)
        onAddNewEvent(ev)
    }

    function monthSpanToDates(startMonth, endMonth, baseYear) {
        const start = new Date(baseYear, startMonth, 1)
        let endYear = baseYear
        if (endMonth < startMonth) endYear += 1
        return { start, end: new Date(endYear, endMonth + 1, 0) }
    }

    function daysBetween(d1, d2) {
        return Math.ceil(Math.abs(new Date(d2) - new Date(d1)) / (24 * 60 * 60 * 1000))
    }

    function calcExactDay(keyMonth, min, max, inv, climateInfo, baseYear) {
        const xs = [0, 30, 60]
        const prevMonth = (keyMonth + 11) % 12
        const nextMonth = (keyMonth + 1) % 12
        const ymins = [climateInfo[months[prevMonth]+"Min"], climateInfo[months[keyMonth]+"Min"], climateInfo[months[nextMonth]+"Min"]].map(parseFloat)
        const ymaxs = [climateInfo[months[prevMonth]+"Max"], climateInfo[months[keyMonth]+"Max"], climateInfo[months[nextMonth]+"Max"]].map(parseFloat)
        const minS = new Spline(xs, ymins), maxS = new Spline(xs, ymaxs)
        let kd = null
        if (!inv) { for (let i = 0;  i <= 60; i++) { if (minS.at(i) >= min && maxS.at(i) <= max) { kd = i; break } } }
        else      { for (let i = 60; i >= 0;  i--) { if (minS.at(i) >= min && maxS.at(i) <= max) { kd = i; break } } }
        if (kd == null) return null
        let py = baseYear
        if (prevMonth > keyMonth && seasonRotation !== "Winter") py -= 1
        const d = new Date(py, prevMonth, 15); d.setDate(d.getDate() + kd)
        return d
    }

    // Build month-by-month climate rows around the planting window (same shape
    // the Edit Card chart uses). series = [{name:"Hot...",data:[...]},{name:"Cold...",data:[...]}]
    function toRechartsData(series, seedISO, harvestISO) {
        if (!series || !Array.isArray(series)) return []
        if (!seedISO || !harvestISO) return []
        const seed = new Date(seedISO), harvest = new Date(harvestISO)
        if (isNaN(seed) || isNaN(harvest)) return []
        const hotSeries  = series.find(s => s?.name?.toLowerCase().includes("hot"))
        const coldSeries = series.find(s => s?.name?.toLowerCase().includes("cold"))
        if (!hotSeries || !Array.isArray(hotSeries.data)) return []
        if (!coldSeries || !Array.isArray(coldSeries.data)) return []
        const hot = hotSeries.data, cold = coldSeries.data
        const MONTHS_BEFORE = 4, MONTHS_AFTER = 4
        const start = new Date(Date.UTC(seed.getUTCFullYear(), seed.getUTCMonth() - MONTHS_BEFORE, 1))
        const end   = new Date(Date.UTC(harvest.getUTCFullYear(), harvest.getUTCMonth() + MONTHS_AFTER, 1))
        const rows = []
        let cursor = new Date(start)
        while (cursor <= end) {
            const year = cursor.getUTCFullYear(), month = cursor.getUTCMonth(), idx = month % 12
            rows.push({
                date: cursor.getTime(),
                iso: cursor.toISOString().split("T")[0],
                hot: hot[idx] ?? hot.at(-1) ?? null,
                cold: cold[idx] ?? cold.at(-1) ?? null,
            })
            cursor = new Date(Date.UTC(year, month + 1, 1))
        }
        return rows
    }

    function findNearestRow(targetTime, data) {
        if (!Array.isArray(data) || data.length === 0 || isNaN(targetTime)) return null
        return data.reduce((closest, row) => {
            const rowTime = typeof row.date === "number" ? row.date : Date.parse(row.date)
            const closestTime = typeof closest.date === "number" ? closest.date : Date.parse(closest.date)
            return Math.abs(rowTime - targetTime) < Math.abs(closestTime - targetTime) ? row : closest
        }, data[0])
    }

    // Compute and store the recharts window chart from a plant's seed/harvest dates.
    function buildPlantingWindowChart(climateSeries, plant) {
        const rows = toRechartsData(climateSeries, plant.seedStartDate, plant.harvestDate)
        setRechartsClimate(rows)
        const seed = Date.parse(plant.seedStartDate), harvest = Date.parse(plant.harvestDate)
        setMinCold(findNearestRow(seed, rows)?.cold ?? 0)
        setMaxHot(findNearestRow(harvest, rows)?.hot ?? 100)
        setWindowDates({ seed, harvest })
    }

    function calcPlantingCalendar(plant) {
        calcMetaRef.current = null
        const plantInfo = { ...plant }
        let min = parseFloat(plantInfo.minimumTemp), max = parseFloat(plantInfo.maximumTemp)
        let climateInfo = Object.assign({}, climateData)
        if (profile?.climates?.length > 0) { const sel = profile.climates.find(c => c.selected); if (sel) climateInfo = sel }
        else if (unitmeasurement === "metric") { min = min * 9/5 + 32; max = max * 9/5 + 32 }

        const seasonRanges = { Fall:[9,10,11], Winter:[0,1,2], Spring:[3,4,5], Summer:[6,7,8] }
        const seasonMonths = seasonRanges[seasonRotation]
        let plWindow = months.reduce((acc, m, i) => { if (parseFloat(climateInfo[m+"Min"]) >= min && parseFloat(climateInfo[m+"Max"]) <= max) acc.push(i); return acc }, [])

        const climateSeries = [
            { name:"Hot Averages", color:"#F00505", data: months.map(m => parseFloat(climateInfo[m+"Max"])) },
            { name:"Cold Averages", color:"#143FA1", data: months.map(m => parseFloat(climateInfo[m+"Min"])) },
        ]
        setClimateAverages(climateSeries)
        if (plWindow.length < 2) { setPlantingDates(false); setPlantingCalendarModal(true); return }

        let ranges = []
        let start = plWindow[0], pointer = start
        for (let i = 1; i < plWindow.length; i++) {
            const cur = plWindow[i]
            if (cur === pointer + 1 || (pointer === 11 && cur === 0)) { pointer = cur }
            else { ranges.push({ start, end: pointer }); start = cur; pointer = cur }
        }
        ranges.push({ start, end: pointer })
        ranges = ranges.map(r => {
            const adj = seasonMonths.includes(r.start) ? r.start : seasonMonths.find(m => m >= r.start) || seasonMonths[0]
            return seasonMonths.includes(adj) ? { start: adj, end: r.end } : null
        }).filter(Boolean)

        if (ranges.length === 0) { setPlantingDates(false); setPlantingCalendarModal(true); return }

        const daysToTransplant = parseInt(plantInfo.daysToTransplant) || 0
        const coarse = monthSpanToDates(ranges[0].start, ranges[0].end, currYear)
        let sMonth = ranges[0].start, sYear = coarse.start.getFullYear(), startDay = null
        do { startDay = calcExactDay(sMonth, min, max, false, climateInfo, sYear); if (!startDay || isNaN(startDay)) { sMonth = (sMonth+1)%12; if (sMonth===0) sYear++ } } while (!(startDay instanceof Date && !isNaN(startDay)))
        let endDay = calcExactDay(ranges[0].end, min, max, true, climateInfo, coarse.end.getFullYear())
        if (endDay < startDay) endDay.setFullYear(endDay.getFullYear() + 1)
        const numDays = (endDay - startDay) / (1000*3600*24)

        if (numDays > parseFloat(plantInfo.daysToHarvest)) {
            const annot = { xaxis:[{ x:startDay.getTime(), x2:endDay.getTime(), borderColor:"#00E396", fillColor:"#00E396", opacity:0.2 }], yaxis:[{ y:min, y2:max, borderColor:"#00E396", fillColor:"#00E396", opacity:0.2 }] }
            setAnnotations(annot)
            const today = new Date()
            const fmt = d => `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`
            const dayMs = 1000 * 60 * 60 * 24

            let seed, transplant, harvest
            if (!lastDatePossible) {
                harvest = new Date(startDay); harvest.setDate(harvest.getDate() + parseInt(plantInfo.daysToHarvest))
                seed = new Date(startDay); seed.setDate(seed.getDate() - daysToTransplant - parseInt(plantInfo.germinationTime))
                transplant = new Date(startDay)
            } else {
                harvest = new Date(endDay)
                transplant = new Date(endDay); transplant.setDate(transplant.getDate() - parseInt(plantInfo.daysToHarvest))
                seed = new Date(endDay); seed.setDate(seed.getDate() - daysToTransplant - parseInt(plantInfo.daysToHarvest) - parseInt(plantInfo.germinationTime))
            }

            // If the natural seed-start is in the past, shift forward so seed starts
            // today; only roll to next year if the cycle can't finish within this
            // year's window.
            let rollInfo = null
            if (seed < today) {
                const deltaDays = Math.ceil((today - seed) / dayMs)
                const projectedHarvest = new Date(harvest); projectedHarvest.setDate(projectedHarvest.getDate() + deltaDays)
                if (projectedHarvest <= endDay) {
                    [seed, transplant, harvest].forEach(d => d.setDate(d.getDate() + deltaDays))
                } else {
                    [seed, transplant, harvest].forEach(d => d.setFullYear(d.getFullYear() + 1))
                    // Capture why it can't fit this year, for the confirmation modal.
                    rollInfo = {
                        year: harvest.getFullYear(),
                        daysToHarvest: parseInt(plantInfo.daysToHarvest) || 0,
                        windowStart: fmt(startDay),
                        windowEnd: fmt(endDay),
                        projectedHarvest: fmt(projectedHarvest),
                    }
                }
            }

            plant.seedStartDate = fmt(seed); plant.transplantDate = fmt(transplant); plant.harvestDate = fmt(harvest)

            if (rollInfo) {
                // Doesn't fit this year — defer events/chart; createNewPlant asks the
                // user whether to set it up in next year's area.
                return rollInfo
            }

            // Stash the placed dates + window end so createNewPlant can build succession plantings.
            calcMetaRef.current = { seed: new Date(seed), transplant: new Date(transplant), harvest: new Date(harvest), windowEnd: new Date(endDay) }
            createNewEvent(plant, `${plant.species} - Seed Starting`, "Seed Starting", seed)
            createNewEvent(plant, `${plant.species} - Transplanting`, "Transplanting", transplant)
            createNewEvent(plant, `${plant.species} - Harvesting`, "Harvest", harvest)
            buildPlantingWindowChart(climateSeries, plant)
            setPlantingDates(true)
            setPlantingCalendarModal(true)
            return null
        } else {
            setPlantingDates(false)
            setPlantingMonths({ start: startDay.toDateString(), end: endDay.toDateString(), diff: daysBetween(startDay, endDay) })
            setPlantingCalendarModal(true)
            return null
        }
    }

    // ── area CRUD ─────────────────────────────────────────────────────────────
    function addArea() {
        setCurrArea({ name:"", id:"", year:currYear, info:{ totalSpace:0 }, history:{}, plants:[] })
        setAddAreaModal(true)
    }

    function createNewArea() {
        if (copyFromPrevious) {
            if (!window.confirm("Are you sure? This process may copy multiple areas.")) return
            const prevAreas = userareas.filter(a => a.year === currYear - 1 && (a.name||"").toLowerCase() !== "unassigned")
            prevAreas.forEach(old => {
                const perennials = (old.plants||[]).filter(p => (p.season||"").toLowerCase() === "perennial").map(p => ({
                    ...p, id:uuidv4(), climateAverages:[], harvests:[], notes:[], harvestTotals:0, totalHarvest:0,
                    seedStartDate: shiftOneYear(p.seedStartDate), transplantDate: shiftOneYear(p.transplantDate), harvestDate: shiftOneYear(p.harvestDate),
                }))
                onAddArea({ id:uuidv4(), name:old.name, year:currYear, info:{...old.info}, plants:perennials, history:{} })
            })
            setAddAreaModal(false); return
        }
        const errs = {}
        if (!space) errs.space = "Total space is required"
        if (!name)  errs.name  = "Area name is required"
        if (userareas.some(a => a.name === name && a.year === currYear)) errs.name = "Area with this name already exists for this year"
        setErrors(errs)
        if (Object.keys(errs).length) return
        onAddArea({ name, id:uuidv4(), year:currYear, info:{ totalSpace:space }, history:{}, plants:[] })
        setAddAreaModal(false)
    }

    function shiftOneYear(str) {
        if (!str) return null
        const d = new Date(str); if (isNaN(d)) return null
        d.setFullYear(d.getFullYear() + 1); return d.toISOString()
    }

    function cancelNewArea() { setAddAreaModal(false) }

    // ── plant CRUD ────────────────────────────────────────────────────────────
    function addPlant(areaIndex) {
        setCurrArea(userareas[areaIndex])
        setCurrPlant({ species:"", variety:"", id:uuidv4(), icon:"", seedStartDate:"", transplantDate:"", harvestDate:"", status:"Seed Starting", numPlants:"", harvests:[], notes:[], harvestTotals:0 })
        setSuccession(false)
        setAddPlantModal(true)
    }

    function updateSpecies(species) {
        const np = { ...currPlant, species }
        if (plantMap[species]) { Object.keys(plantProps).forEach(k => { np[k] = plantMap[species][k] }) }
        else {
            const cp = customplants.find(p => p.species === species)
            if (cp) Object.assign(np, { daysToHarvest:cp.daysToHarvest, daysToTransplant:cp.daysToTransplant, germinationTime:cp.germinationTime, minimumTemp:cp.minimumTemperature, maximumTemp:cp.maximumTemperature, yield_per_plant_ounces:cp.yieldPerPlant, plant_family:cp.plantFamily, season:cp.season_rotation, size:cp.size, spacing_between_plants:cp.spacing_between_plants, guildFunction:cp.guildFunction, foodForestLayer:cp.foodForestLayer, climate:cp.climate, USDAMin:cp.USDAMin, USDAMax:cp.USDAMax, designerIconKey:cp.designerIconKey, cardIconKey:cp.cardIconKey, isCustomPlant:true })
        }
        setCurrPlant(np)
    }

    function cancelNewPlant() { setAddPlantModal(false) }

    // Build the succession plantings that follow the first one, stepping by the chosen
    // interval (or "consecutive" = next transplant 1 week after the prior harvest) and
    // stopping once a planting can't finish before the window closes. Each is its own plant.
    function buildSuccessionPlants(base, meta) {
        const germ = parseInt(base.germinationTime || 0)
        const dtt  = parseInt(base.daysToTransplant || 0)
        const dth  = parseInt(base.daysToHarvest || 0)
        const consecutive = successionWeeks === "consecutive"
        const stepDays = (parseInt(successionWeeks) || 2) * 7
        const fmt = d => `${d.getMonth()+1}/${d.getDate()}/${d.getFullYear()}`
        const windowEnd = meta.windowEnd
        const extras = []
        let curSeed = new Date(meta.seed), curHarvest = new Date(meta.harvest)
        for (let i = 0; i < 26; i++) {   // defensive cap
            let s
            if (consecutive) { s = new Date(curHarvest); s.setDate(s.getDate() + 7 - (germ + dtt)) }
            else { s = new Date(curSeed); s.setDate(s.getDate() + stepDays) }
            const t = new Date(s); t.setDate(t.getDate() + germ + dtt)
            const h = new Date(t); h.setDate(h.getDate() + dth)
            if (h > windowEnd) break
            const clone = { ...base, id: uuidv4(), events: [], harvests: [], notes: [], harvestTotals: 0, totalHarvest: 0,
                seedStartDate: fmt(s), transplantDate: fmt(t), harvestDate: fmt(h) }
            createNewEvent(clone, `${clone.species} - Seed Starting`, "Seed Starting", s)
            createNewEvent(clone, `${clone.species} - Transplanting`, "Transplanting", t)
            createNewEvent(clone, `${clone.species} - Harvesting`, "Harvest", h)
            extras.push(clone)
            curSeed = s; curHarvest = h
        }
        // TEMP DIAGNOSTIC — remove once succession matches the crop planner
        console.warn("[PA succession]", base.species, {
            seasonRotation, dth, germ, dtt, weeks: successionWeeks,
            firstSeed: new Date(meta.seed).toDateString(),
            windowEnd: new Date(meta.windowEnd).toDateString(),
            extras: extras.length,
            total: extras.length + 1,
            seeds: [fmt(new Date(meta.seed)), ...extras.map(e => e.seedStartDate)],
        })
        return extras
    }

    function createNewPlant() {
        const errs = {}
        if (!numPlants)   errs.numPlants = "Number of Plants is required"
        if (!plantStatus) errs.status    = "Plant Status is required"
        if (plantStatus === "Purchased" && !plantSource) errs.plantSource = "Plant Source is required"
        if (plantStatus === "Purchased" && !orderDate)   errs.orderDate   = "Order Date is required"
        if (!currPlant.species) errs.species = "Plant species is required"
        if ((currPlant.season === "annual" || currPlant.season === "biennial") && !seasonRotation) errs.season = "Season is required for annuals/biennials"
        if (Object.keys(errs).length) { setErrors(errs); return }

        const plant = { ...currPlant, variety, numPlants, status:plantStatus, season_rotation:seasonRotation }
        if (plantStatus === "Purchased") Object.assign(plant, { plantSource, orderDate, expectedDeliveryDate })
        const numPerSqFt = Math.pow(12 / (plant.spacing_between_plants || 1), 2)
        plant.spaceUsed    = (numPlants / numPerSqFt).toFixed(2)
        plant.totalHarvest = 0
        plant.estHarvest   = plant.yield_per_plant_ounces ? plant.yield_per_plant_ounces * numPlants / 16.0 : 0
        if (profile?.unitmeasurement === "metric") {
            plant.spaceUsed              = (plant.spaceUsed / 10.764).toFixed(2)
            plant.estHarvest             = (plant.estHarvest / 2.2).toFixed(2)
            plant.yield_per_plant_ounces = (plant.yield_per_plant_ounces / 35.274).toFixed(2)
            plant.minimumTemp            = ((plant.minimumTemp - 32) * 5 / 9).toFixed(2)
            plant.maximumTemp            = ((plant.maximumTemp - 32) * 5 / 9).toFixed(2)
            plant.spacing_between_plants = (plant.spacing_between_plants / 2.54).toFixed(2)
        }
        const dth = parseInt(plant.daysToHarvest || 0)
        if (plant.season === "annual" || plant.season === "biennial") {
            plant.events = []
            const roll = calcPlantingCalendar(plant)
            if (roll) {
                // Window can't fit this year — ask before placing it in next year's area.
                // (Option 1: a rolled planting is single — no cross-year succession.)
                setPendingRollPlant(plant)
                setRollTargetYear(roll.year)
                setRollInfo(roll)
                setRollModalOpen(true)
                setAddPlantModal(false)
                setErrors({})
                return
            }
            // Succession: first planting was placed above; generate the rest through the window.
            if (succession && dth > 0 && dth < 65 && calcMetaRef.current) {
                const extras = buildSuccessionPlants(plant, calcMetaRef.current)
                if (extras.length) {
                    onUpdateArea({ ...currArea, plants: [...(currArea.plants||[]), plant, ...extras] })
                    setAddPlantModal(false); setErrors({})
                    return
                }
            }
        }
        onUpdateArea({ ...currArea, plants: [...(currArea.plants||[]), plant] })
        setAddPlantModal(false); setErrors({})
    }

    // Roll-to-next-year confirmation handlers
    function confirmRollToNextYear() {
        const plant = pendingRollPlant
        if (!plant) { setRollModalOpen(false); return }
        // Dates were already set to next year by calcPlantingCalendar; create the deferred events now.
        createNewEvent(plant, `${plant.species} - Seed Starting`, "Seed Starting", new Date(plant.seedStartDate))
        createNewEvent(plant, `${plant.species} - Transplanting`, "Transplanting", new Date(plant.transplantDate))
        createNewEvent(plant, `${plant.species} - Harvesting`, "Harvest", new Date(plant.harvestDate))

        const target = userareas.find(a => a.name === currArea.name && a.year === rollTargetYear)
        if (target) {
            onUpdateArea({ ...target, plants: [...(target.plants || []), plant] })
        } else {
            // Create next year's area from this area, copying only perennials (drop annuals).
            const perennials = (currArea.plants || [])
                .filter(p => (p.season || "").toLowerCase() === "perennial")
                .map(p => ({
                    ...p, id: uuidv4(), climateAverages: [], harvests: [], notes: [], harvestTotals: 0, totalHarvest: 0,
                    seedStartDate: shiftOneYear(p.seedStartDate), transplantDate: shiftOneYear(p.transplantDate), harvestDate: shiftOneYear(p.harvestDate),
                }))
            onAddArea({ id: uuidv4(), name: currArea.name, year: rollTargetYear, info: { ...currArea.info }, plants: [...perennials, plant], history: {} })
        }
        setCurrYear(rollTargetYear)
        setRollModalOpen(false); setPendingRollPlant(null); setRollTargetYear(null); setErrors({})
    }

    function declineRollToNextYear() {
        const plant = pendingRollPlant
        if (plant) {
            // Add to the current area without planting dates / events.
            plant.seedStartDate = ""; plant.transplantDate = ""; plant.harvestDate = ""; plant.events = []
            onUpdateArea({ ...currArea, plants: [...(currArea.plants || []), plant] })
        }
        setRollModalOpen(false); setPendingRollPlant(null); setRollTargetYear(null); setErrors({})
    }

    function deletePlant(areaId, plantIndex) {
        const area = userareas.find(a => a.id === areaId)
        if (!area) return
        const newArea = { ...area, plants: [...area.plants] }
        const [plant] = newArea.plants.splice(plantIndex, 1)
        if (plant?.events?.length) plant.events.forEach(eid => onDeleteEvent(eid))
        if (designs?.[0]) {
            const upd = { ...designs[0], plants: designs[0].plants.filter(p => p.id !== plant.id) }
            if (upd.plants.length !== designs[0].plants.length) onSaveDesign(upd)
        }
        const toNum = v => v == null ? 0 : typeof v === "number" ? v : (v?.N != null ? parseFloat(v.N) : parseFloat(v) || 0)
        newArea.spaceUsed = newArea.plants
            .filter(p => p.status !== "Complete" && p.status !== "Failed")
            .reduce((sum, p) => sum + toNum(p.spaceUsed), 0)
        onUpdateArea(newArea)
    }

    function addHarvest(plant, date, amount) {
        const { areaIndex, plantIndex } = findPlantIndices(userareas, plant.id)
        if (areaIndex === -1 || plantIndex === -1) return
        // Append to the CURRENT snapshot's plant (all persisted harvests), immutably.
        const area = userareas[areaIndex]
        const current = area.plants[plantIndex]
        const existing = Array.isArray(current.harvests) ? current.harvests : (current.harvests?.L || [])
        const newHarvests = [...existing, { date, amount }]
        const totalHarvest = newHarvests.reduce((s, h) => s + parseFloat(h.amount || 0), 0)
        plant.harvests = newHarvests
        plant.totalHarvest = totalHarvest
        const updatedArea = { ...area, plants: area.plants.map((p, i) => i === plantIndex ? { ...p, harvests: newHarvests, totalHarvest } : p) }
        onUpdateArea(updatedArea)
    }

    const deleteHarvest = (date, plant) => {
        const { areaIndex, plantIndex } = findPlantIndices(userareas, plant.id)
        if (areaIndex === -1 || plantIndex === -1) return
        const area = userareas[areaIndex]
        const current = area.plants[plantIndex]
        const existing = Array.isArray(current.harvests) ? current.harvests : (current.harvests?.L || [])
        const idx = existing.findIndex(h => h.date === date)
        if (idx === -1) return
        const newHarvests = existing.filter((_, i) => i !== idx)
        const totalHarvest = newHarvests.reduce((s, h) => s + parseFloat(h.amount || 0), 0)
        plant.harvests = newHarvests
        plant.totalHarvest = totalHarvest
        const updatedArea = { ...area, plants: area.plants.map((p, i) => i === plantIndex ? { ...p, harvests: newHarvests, totalHarvest } : p) }
        onUpdateArea(updatedArea)
    }

    function addPlantLog(plant) {
        const area = userareas.find(a => a.plants?.some(p => p.id === plant.id))
        if (!area) return
        const date = noteDate ? dayjs(noteDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")
        onCreateJournalEntry({ username, area_id:area.id, plant_id:plant.id, note_text:notes||"", tags:noteTags||"", date, photos:[] })
    }

    const deleteNote = (noteId) => { onDeleteJournalEntry(noteId) }

    // ── transfer ──────────────────────────────────────────────────────────────
    function transferPlant(areaIndex, plantIndex) {
        const area = userareas[areaIndex]
        setCurrArea(area)
        const plant = area.plants[plantIndex]
        if (plant) { setTransferModal(true); setCurrPlant(plant) }
    }

    const closeTransferModal = () => { setTransferModal(false) }

    const handleTransferRowChange = (index, field, value) => {
        const updated = [...transferRows]; updated[index] = { ...updated[index], [field]: value }; setTransferRows(updated)
    }
    const addTransferRow    = () => { setTransferRows([...transferRows, { areaId:"", numPlants:"" }]) }
    const removeTransferRow = (i) => { const updated = [...transferRows]; updated.splice(i, 1); setTransferRows(updated) }

    const handleTransfer = async () => {
        const total = transferRows.reduce((s, r) => s + Number(r.numPlants || 0), 0)
        if (total !== parseInt(currPlant.numPlants)) { setTransferError(`You must allocate exactly ${currPlant.numPlants} plants. Currently allocated: ${total}.`); return }
        try {
            const promises = transferRows.map(row => {
                const area = userareas.find(a => a.id === row.areaId)
                if (!area) return Promise.resolve()
                const newPlant = { ...currPlant, numPlants: row.numPlants }
                newPlant.estHarvest = (newPlant.yield_per_plant_ounces || 0) * newPlant.numPlants / 16.0
                newPlant.spaceUsed  = (newPlant.numPlants / Math.pow(12 / (newPlant.spacing_between_plants||1), 2)).toFixed(2)
                return onUpdateArea({ ...area, plants: [...(area.plants||[]), newPlant] })
            })
            if (currArea) promises.push(onUpdateArea({ ...currArea, plants: currArea.plants.filter(p => p.id !== currPlant.id) }))
            await Promise.all(promises)
            setTransferError(""); setTransferModal(false)
        } catch { setTransferError("An error occurred while updating garden areas.") }
    }

    // ── year ──────────────────────────────────────────────────────────────────
    const handlePrevYear = () => setCurrYear(y => y - 1)
    const handleNextYear = () => setCurrYear(y => y + 1)

    // ── render ────────────────────────────────────────────────────────────────
    return (
        <div className="page-content">
            <Container fluid>

                {/* Toolbar */}
                <div className="mb-3" style={{ display:"flex", alignItems:"center", gap:"0.5rem" }}>
                    <div style={{ display:"flex", alignItems:"center" }}>
                        <button type="button" className="btn btn-sm" style={{ background:"none", border:"none", color:"#212529", padding:"0 4px" }} onClick={handlePrevYear}>
                            <i className="bx bx-chevron-left" style={{ fontSize:20 }} />
                        </button>
                        <span style={{ fontWeight:600, fontSize:16 }}>{currYear}</span>
                        <button type="button" className="btn btn-sm" style={{ background:"none", border:"none", color:"#212529", padding:"0 4px" }} onClick={handleNextYear}>
                            <i className="bx bx-chevron-right" style={{ fontSize:20 }} />
                        </button>
                    </div>
                    <button
                        type="button"
                        className="btn btn-sm btn-link"
                        style={{ color:"#212529", textDecoration:"underline", padding:"0.25rem 0.5rem" }}
                        onClick={() => setShowCompleted(v => !v)}
                    >
                        {showCompleted ? "Hide Done" : "Show Done"}
                    </button>
                    <div style={{ marginLeft:"auto" }}>
                        <button type="button" className="btn btn-sm" style={{ backgroundColor:"#FF9900", borderColor:"#FF9900", color:"#fff" }} onClick={addArea}>
                            <i className="bx bx-add-to-queue" style={{ fontSize:16, marginRight:6 }} />
                            Add Area
                        </button>
                    </div>
                </div>

                {/* Area cards */}
                {areas.filter((area, i) => { areaIndices[area.id] = i; return area.year === currYear }).map((area, index) => (
                    <Area
                        key={area.id}
                        area={area}
                        areaIndex={areaIndices[area.id]}
                        areas={areas}
                        userareas={areas}
                        setAreas={setAreas}
                        currYear={currYear}
                        showCompleted={showCompleted}
                        profile={profile}
                        unitmeasurement={unitmeasurement}
                        guildResults={guildResults}
                        plantMap={plantMap}
                        climateAverages={climateAverages}
                        addPlant={addPlant}
                        deletePlant={deletePlant}
                        calcPlantingCalendar={calcPlantingCalendar}
                        createNewPlant={createNewPlant}
                        addPlantModal={addPlantModal}
                        setAddPlantModal={setAddPlantModal}
                        transferPlant={transferPlant}
                        transferModal={transferModal}
                        setTransferModal={setTransferModal}
                        areaExpand={areaExpand}
                        setAreaExpand={setAreaExpand}
                        addPlantLog={addPlantLog}
                        deleteNote={deleteNote}
                        addHarvest={addHarvest}
                        deleteHarvest={deleteHarvest}
                        noteDate={noteDate} setNoteDate={setNoteDate}
                        notes={notes} setNotes={setNotes}
                        noteTags={noteTags} setNoteTags={setNoteTags}
                        space={space} setTotalSpace={setTotalSpace}
                        currPlant={currPlant} setCurrPlant={setCurrPlant}
                        variety={variety} numPlants={numPlants}
                        plantingCalendarOptions={plantingCalendarOptions}
                        onUpdateArea={onUpdateArea}
                        onDeleteArea={onDeleteArea}
                        onUpdateCustomPlant={onUpdateCustomPlant}
                        onSaveCustomPlant={onSaveCustomPlant}
                        onGetCustomPlants={onGetCustomPlants}
                        onSearchGuild={onSearchGuild}
                        timelineNotes={timelineNotes}
                        onCreateJournalEntry={onCreateJournalEntry}
                        onUpdateJournalEntry={onUpdateJournalEntry}
                        username={username}
                    />
                ))}
            </Container>

            {/* Add Plant Modal */}
            <Modal isOpen={addPlantModal} toggle={cancelNewPlant}>
                <ModalHeader tag="h4">New Plant</ModalHeader>
                <ModalBody>
                    <Form>
                        <Row className="mb-3">
                            <Col><span style={{ color:"#7F8F7B", fontSize:"0.9rem" }}>* Adding to: <b>{currArea.name}</b></span></Col>
                        </Row>
                        <Row className="mb-2">
                            <Col><b>Species</b></Col>
                            <Col>
                                <PlantSelect plantOptions={plantOptions.map(p => ({ key:p.value, label:p.value }))} value="" onChange={updateSpecies} />
                                <span style={{ color:"#FF0000" }}>{errors.species}</span>
                            </Col>
                        </Row>
                        {currPlant.season !== "perennial" && (
                            <Row className="mb-2">
                                <Col md={6}><b>Season</b></Col>
                                <Col md={6}>
                                    <div style={{ display:"flex", gap:"0.4rem", flexWrap:"wrap" }}>
                                        {["Spring","Summer","Fall","Winter"].map(s => (
                                            <button
                                                key={s}
                                                type="button"
                                                className="btn btn-sm"
                                                style={seasonRotation === s
                                                    ? { backgroundColor:"#7F8F7B", borderColor:"#7F8F7B", color:"#fff" }
                                                    : { backgroundColor:"#fff", borderColor:"#d1d5db", color:"#212529" }}
                                                onClick={() => setSeasonRotation(prev => prev === s ? "" : s)}
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                    <span style={{ color:"#FF0000" }}>{errors.season}</span>
                                </Col>
                            </Row>
                        )}
                        <Row className="mb-2">
                            <Col md={6}><b>Variety</b></Col>
                            <Col md={6}><Input type="text" value={variety} onChange={e => setVariety(e.target.value)} /></Col>
                        </Row>
                        <Row className="mb-2">
                            <Col md={6}><b>Growing Phase</b></Col>
                            <Col md={6}>
                                <Input type="select" onChange={e => setPlantStatus(e.target.value)}>
                                    <option value=""></option>
                                    {["Purchased","Not Started","Seed Starting","Transplanting","Growing","Harvest","Failed","Completed"].map(s => <option key={s}>{s}</option>)}
                                </Input>
                                <span style={{ color:"#FF0000" }}>{errors.status}</span>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}><b>Number of Plants</b></Col>
                            <Col md={6}><Input type="text" value={numPlants} onChange={e => setNumPlants(e.target.value)} /><span style={{ color:"#FF0000" }}>{errors.numPlants}</span></Col>
                        </Row>
                        {currPlant.season !== "perennial" && parseInt(currPlant.daysToHarvest || 0) > 0 && parseInt(currPlant.daysToHarvest || 0) < 65 && (
                            <Row className="mb-3">
                                <Col md={6}><b>Succession plant</b></Col>
                                <Col md={6}>
                                    <Input type="checkbox" checked={succession} onChange={e => setSuccession(e.target.checked)} style={{ marginTop:0 }} />
                                    {succession && (
                                        <Input type="select" className="mt-2" value={successionWeeks} onChange={e => setSuccessionWeeks(e.target.value)}>
                                            <option value="2">Every 2 weeks</option>
                                            <option value="3">Every 3 weeks</option>
                                            <option value="4">Every 4 weeks</option>
                                            <option value="consecutive">Consecutive (after harvest)</option>
                                        </Input>
                                    )}
                                </Col>
                            </Row>
                        )}
                        {plantStatus === "Purchased" && (<>
                            <Row className="mb-2"><Col md={6}><b>Source</b></Col><Col md={6}><Input type="text" value={plantSource} onChange={e => setPlantSource(e.target.value)} /></Col></Row>
                            <Row className="mb-2"><Col md={6}><b>Order Date</b></Col><Col md={6}><Input type="date" value={orderDate} onChange={e => setOrderDate(e.target.value)} /></Col></Row>
                            <Row className="mb-2"><Col md={6}><b>Expected Delivery</b></Col><Col md={6}><Input type="text" value={expectedDeliveryDate} onChange={e => setExpectedDeliveryDate(e.target.value)} /></Col></Row>
                        </>)}
                        <Row className="mt-3">
                            <Col md={4} />
                            <Col md={2}><button type="button" className="btn btn-sm" style={{ backgroundColor:"#FF9900", borderColor:"#FF9900", color:"#fff" }} onClick={createNewPlant}>Create</button></Col>
                            <Col md={2}><button type="button" className="btn btn-sm" style={{ backgroundColor:"#fff", borderColor:"#d1d5db", color:"#212529" }} onClick={cancelNewPlant}>Cancel</button></Col>
                        </Row>
                    </Form>
                </ModalBody>
            </Modal>

            {/* Add Area Modal */}
            <Modal isOpen={addAreaModal} toggle={() => {}}>
                <ModalHeader tag="h4">New Area</ModalHeader>
                <ModalBody>
                    <Form>
                        {userareas.filter(a => a.year === currYear - 1 && (a.name||"").toLowerCase() !== "unassigned").length > 0 && (
                            <Row style={{ paddingBottom:10 }}>
                                <Col md={6}><b>Copy areas from last year</b></Col>
                                <Col md={6}><Input type="checkbox" checked={copyFromPrevious} onChange={e => setCopyFromPrevious(e.target.checked)} /></Col>
                            </Row>
                        )}
                        {!copyFromPrevious && (<>
                            <Row>
                                <Col md={6}><b>Name</b></Col>
                                <Col md={6}><Input type="text" value={name} onChange={e => setName(e.target.value)} /><span style={{ color:"#FF0000" }}>{errors.name}</span></Col>
                            </Row>
                            <Row>
                                <Col md={6}><b>Total Space ({unitmeasurement === "imperial" ? "ft²" : "m²"})</b></Col>
                                <Col md={6}><Input type="text" value={space} onChange={e => setTotalSpace(e.target.value)} /><span style={{ color:"#FF0000" }}>{errors.space}</span></Col>
                            </Row>
                        </>)}
                        <Row style={{ paddingTop:10 }}>
                            <Col md={4} />
                            <Col md={2}><button type="button" className="btn btn-sm" style={{ backgroundColor:"#FF9900", borderColor:"#FF9900", color:"#fff" }} onClick={createNewArea}>Create</button></Col>
                            <Col md={2}><button type="button" className="btn btn-sm" style={{ backgroundColor:"#fff", borderColor:"#d1d5db", color:"#212529" }} onClick={cancelNewArea}>Cancel</button></Col>
                        </Row>
                    </Form>
                </ModalBody>
            </Modal>

            {/* Transfer Modal */}
            <Modal isOpen={transferModal} toggle={closeTransferModal}>
                <ModalHeader tag="h4">Transfer Plant to Other Areas</ModalHeader>
                <ModalBody>
                    {transferError && <Alert color="danger" toggle={() => setTransferError("")}>{transferError}</Alert>}
                    <Row><Col md={6}><strong>Plant:</strong></Col><Col>{currPlant.name || currPlant.species}</Col></Row>
                    <Row><Col md={6}><strong>Variety:</strong></Col><Col>{currPlant.variety}</Col></Row>
                    <Row><Col md={6}><strong>Available Plants:</strong></Col><Col>{currPlant.numPlants}</Col></Row>
                    <Row><Col md={6}><strong>Remaining to Assign:</strong></Col><Col>{remainingPlants}</Col></Row>
                    <Row className="mb-1 fw-bold small text-muted mt-2"><Col md={5}>Area</Col><Col md={4}># of Plants</Col><Col md={3}></Col></Row>
                    {transferRows.map((row, i) => (
                        <Row key={i} className="mb-2 align-items-center">
                            <Col md={5}>
                                <FormGroup className="mb-0">
                                    <Input type="select" value={row.areaId} onChange={e => handleTransferRowChange(i, "areaId", e.target.value)}>
                                        <option value="">Select Area</option>
                                        {userareas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup className="mb-0">
                                    <Input type="number" min="1" max={currPlant.numPlants} value={row.numPlants} onChange={e => handleTransferRowChange(i, "numPlants", e.target.value)} />
                                </FormGroup>
                            </Col>
                            <Col md={3} className="text-end">
                                {transferRows.length > 1 && <i className="fas fa-trash" style={{ cursor:"pointer" }} onClick={() => removeTransferRow(i)} />}
                            </Col>
                        </Row>
                    ))}
                    <div className="mt-3">
                        <span style={{ cursor:"pointer", color:"#007bff", textDecoration:"underline", fontSize:"0.9rem" }} onClick={addTransferRow}>+ Add another area</span>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-sm" style={{ backgroundColor:"#fff", borderColor:"#d1d5db", color:"#212529" }} onClick={closeTransferModal}>Cancel</button>
                    <button type="button" className="btn btn-sm" style={{ backgroundColor:"#FF9900", borderColor:"#FF9900", color:"#fff" }} onClick={handleTransfer} disabled={remainingPlants !== 0}>Transfer</button>
                </ModalFooter>
            </Modal>

            {/* Planting Calendar Modal */}
            <Modal isOpen={plantingCalendarModal} toggle={() => setPlantingCalendarModal(false)}>
                <ModalHeader tag="h4">
                    {plantingDates ? `Planting Window For ${currPlant.species} (in green overlay)` : `No Planting Window for ${currPlant.species}`}
                </ModalHeader>
                <ModalBody>
                    <Row style={{ paddingTop:10 }}>
                        <Col md={12}>
                            {plantingDates && rechartsClimate.length > 0
                                ? (
                                    <ResponsiveContainer width="100%" height={400}>
                                        <AreaChart data={rechartsClimate}>
                                            <XAxis
                                                dataKey="date"
                                                type="number"
                                                scale="time"
                                                domain={['dataMin', 'dataMax']}
                                                tickFormatter={(ts) => dayjs(ts).format("MMM")}
                                            />
                                            <YAxis />
                                            <RechartsArea type="monotone" dataKey="cold" fill="#2563eb" stroke="#1d4ed8" fillOpacity={0.25} />
                                            <RechartsArea type="monotone" dataKey="hot" fill="#f87171" stroke="#ef4444" fillOpacity={0.25} />
                                            <ReferenceArea
                                                x1={windowDates.seed}
                                                x2={windowDates.harvest}
                                                y1={minCold}
                                                y2={maxHot}
                                                fill="#00E396"
                                                fillOpacity={0.5}
                                                stroke="#00E396"
                                            />
                                            <Legend verticalAlign="bottom" height={36} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                )
                                : (<>
                                    <ReactApexChart options={plantingCalendarOptions} series={climateAverages} height={400} type="area" />
                                    <p className="mt-3">Unable to find an adequate planting window for this plant given your climate and season requirements.
                                        {plantingMonths.start && <span> The closest window is from {plantingMonths.start} – {plantingMonths.end}, a total of {plantingMonths.diff} days.</span>}
                                    </p>
                                </>)
                            }
                        </Col>
                    </Row>
                </ModalBody>
            </Modal>

            {/* Roll-to-next-year confirmation */}
            <Modal isOpen={rollModalOpen} toggle={declineRollToNextYear}>
                <ModalHeader tag="h4">Not enough time to grow this year</ModalHeader>
                <ModalBody>
                    <p>
                        <b>{pendingRollPlant?.species}</b> needs about <b>{rollInfo?.daysToHarvest} days</b> to reach harvest after transplanting.
                    </p>
                    <p>
                        Its suitable planting window for {currYear} runs <b>{rollInfo?.windowStart} – {rollInfo?.windowEnd}</b>. Starting now, it wouldn't be ready to harvest until <b>{rollInfo?.projectedHarvest}</b> — past the end of the window — so there isn't enough time to grow it this year.
                    </p>
                    <p>Set it up in your <b>{rollTargetYear}</b> “{currArea.name}” area instead? (If that area doesn't exist yet, it will be created with this year's perennials.)</p>
                </ModalBody>
                <ModalFooter>
                    <button type="button" className="btn btn-sm" style={{ backgroundColor:"#fff", borderColor:"#d1d5db", color:"#212529" }} onClick={declineRollToNextYear}>Add without dates</button>
                    <button type="button" className="btn btn-sm" style={{ backgroundColor:"#FF9900", borderColor:"#FF9900", color:"#fff" }} onClick={confirmRollToNextYear}>Set up for {rollTargetYear}</button>
                </ModalFooter>
            </Modal>
        </div>
    )
}

const mapStateToProps = (state) => {
    const sortedPlants = [...(state.designer.plants || [])].sort((a, b) => a.name > b.name ? 1 : -1)
    const plantFun = sortedPlants.reduce((map, obj) => { map[obj.name] = obj; return map }, {})
    const filteredAreas = (state.goals.areas || [])
        .map(area => { const a = structuredClone(area); a.plants = a.plants.filter(p => p.species !== undefined); return a })
        .sort((a, b) => a.name.localeCompare(b.name))
    return {
        plantMap:     plantFun,
        plantData:    sortedPlants,
        profile:      state.Profile.user || {},
        userareas:    filteredAreas,
        designs:      state.designer.designs || [],
        climateData:  state.designer.temps || {},
        customplants: state.goals.customplants || [],
        guildResults: state.goals.plants || [],
        timelineNotes: Array.isArray(state.journal.timelineNotes) ? state.journal.timelineNotes : [],
    }
}

const mapDispatchToProps = dispatch => ({
    onGetProfile:         username => dispatch(getProfile(username)),
    onGetPlantData:       ()       => dispatch(getPlantData()),
    onGetAreas:           ()       => dispatch(getAreas()),
    onUpdateArea:         area     => dispatch(updateArea(area)),
    onAddArea:            area     => dispatch(addArea(area)),
    onDeleteArea:         id       => dispatch(deleteArea(id)),
    onGetCustomPlants:    ()       => dispatch(fetchCustomPlants()),
    onUpdateCustomPlant:  plant    => dispatch(updateCustomPlant(plant)),
    onSaveCustomPlant:    plant    => dispatch(saveCustomPlant(plant)),
    onGetClimateData:     zipcode  => dispatch(getClimateData(zipcode)),
    onAddNewEvent:        event    => dispatch(addNewEvent(event)),
    onDeleteEvent:        id       => dispatch(deleteEvent(id)),
    onSaveDesign:         design   => dispatch(updateDesign(design)),
    onLoadDesigns:        ()       => dispatch(getDesigns()),
    onSearchGuild:        params   => dispatch(searchGuild(params)),
    onCreateJournalEntry: data     => dispatch(createJournalEntry(data)),
    onDeleteJournalEntry: noteId   => dispatch(deleteJournalEntry(noteId)),
    onGetJournalTimeline: params   => dispatch(getJournalTimeline(params)),
    onUpdateJournalEntry: data     => dispatch(updateJournalEntry(data)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PlantsAreas)
